import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";

import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import {
  callAPI,
  Method,
  handleApiError,
  updateMainHeader,
} from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { HeaderList, IHeaderListActions } from "../../Components/Header/HeaderList";

// Interfaces
interface IVendorOption {
  id: string;
  vendorName: string;
}

interface IVendorBillItem {
  itemName: string;
  qty: number | string;
  rate: number | string;
  tax: number | string;
}

interface IVendorBillForm {
  vendorID: string;
  billDate: string;
  dueDate: string;
  items: IVendorBillItem[];
  subtotal: number | string;
  taxAmount: number | string;
  totalAmount: number | string;
  billNo?: string;
}

// Calculate totals
const calculateTotals = (items: IVendorBillItem[]) => {
  let subtotal = 0,
    taxAmount = 0;
  items.forEach((i) => {
    const qty = Number(i.qty) || 0,
      rate = Number(i.rate) || 0,
      tax = Number(i.tax) || 0;
    subtotal += qty * rate;
    taxAmount += (qty * rate * tax) / 100;
  });
  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    taxAmount: parseFloat(taxAmount.toFixed(2)),
    totalAmount: parseFloat((subtotal + taxAmount).toFixed(2)),
  };
};

export const CreateVendorBill: React.FC<any> = React.memo(() => {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [vendorOptions, setVendorOptions] = useState<IVendorOption[]>([]);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IVendorBillForm>({
    defaultValues: {
      vendorID: "",
      billDate: dayjs().format("YYYY-MM-DD"),
      dueDate: dayjs().format("YYYY-MM-DD"),
      items: [{ itemName: "", qty: 1, rate: 0, tax: 0 }],
      subtotal: 0,
      taxAmount: 0,
      totalAmount: 0,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const watchedItems = watch("items");

  // Fetch vendors & edit data
  useEffect(() => {
    const fetchData = async () => {
      setInitialLoading(true);
      try {
        const response: any = await callAPI("Vendor", Method.GET);
        setVendorOptions(
          (Array.isArray(response) ? response : response?.data || []).map(
            (v: any) => ({
              id: v.id || v._id || "",
              vendorName: v.vendorName || "",
            })
          )
        );
      } catch {
        showMessage("Error fetching vendors", "error");
      }

      if (isEditMode && id) {
        try {
          const response: any = await callAPI(
            `VendorBill/ById/${id}`,
            Method.GET
          );
          const billData = response?.data || response;
          if (billData) {
            reset({
              vendorID: billData.vendorid || billData.vendorID || "",
              billDate: dayjs(billData.billDate).format("YYYY-MM-DD"),
              dueDate: dayjs(billData.dueDate).format("YYYY-MM-DD"),
              billNo: billData.billNo,
              items: billData.items?.length
                ? billData.items.map((i: any) => ({
                    itemName: i.itemName || "",
                    qty: i.qty || 1,
                    rate: i.rate || 0,
                    tax: i.tax || 0,
                  }))
                : [{ itemName: "", qty: 1, rate: 0, tax: 0 }],
              subtotal: billData.subtotal || 0,
              taxAmount: billData.taxAmount || 0,
              totalAmount: billData.totalAmount || 0,
            });
          }
        } catch (err) {
          showMessage("Error fetching Vendor Bill", "error");
          console.error(err);
        }
      }

      setInitialLoading(false);
    };
    fetchData();
  }, [id, isEditMode, reset, showMessage]);

  // Auto-calculate totals
  useEffect(() => {
    const totals = calculateTotals(watchedItems || []);
    setValue("subtotal", totals.subtotal);
    setValue("taxAmount", totals.taxAmount);
    setValue("totalAmount", totals.totalAmount);
  }, [watchedItems, setValue]);

  // Header & breadcrumbs
  useEffect(() => {
    const title = isEditMode ? "Edit Vendor Bill" : "Create Vendor Bill";
    updateMainHeader(title);
    breadCrumbsStore.updateBreadCrumbs([
      { "Vendor Bills": "/vendorbill/list" },
      { [title]: "" },
    ]);
  }, [isEditMode]);

   useEffect(() => {
      const handleEnter = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          formRef.current?.requestSubmit();
        }
      };
      window.addEventListener("keydown", handleEnter);
      return () => window.removeEventListener("keydown", handleEnter);
    }, []);
  

  const handleCancel = () => navigate("/vendorbill/list");

  // Submit handler
  const onSubmit = async (data: IVendorBillForm) => {
    const formattedbillDate: any = dayjs(`${data?.billDate} 00:00`, "DD-MM-YYYY HH:mm").toISOString();
    const formatteddueDate: any = dayjs(`${data?.dueDate} 00:00`, "DD-MM-YYYY HH:mm").toISOString();

    setLoading(true);

    const payload = {
      vendorID: data.vendorID || null,
      billDate: formattedbillDate,
      dueDate: formatteddueDate,
      items: data.items.map((i) => ({
        itemName: i.itemName,
        qty: Number(i.qty) || 0,
        rate: Number(i.rate) || 0,
        tax: Number(i.tax) || 0,
      })),
      subtotal: Number(data.subtotal),
      taxAmount: Number(data.taxAmount),
      totalAmount: Number(data.totalAmount),
      billNo: data.billNo,
    };

    try {
      const method = isEditMode ? Method.PUT : Method.POST;
      const apiId = isEditMode ? id : null;
      const url = isEditMode
        ? `VendorBill`
        : `VendorBill?vendorId=${data.vendorID}`;

      const response: any = await callAPI(url, method, payload, apiId);

      if (response?.error || response?.errors) {
        showMessage(handleApiError(response), "error");
      } else {
        showMessage(
          isEditMode ? "Vendor Bill updated successfully" : "Vendor Bill added successfully",
          "success"
        );
        navigate("/vendorbill/list");
      }
    } catch {
      showMessage("Error submitting Vendor Bill", "error");
    } finally {
      setLoading(false);
    }
  };

  const actions: IHeaderListActions[] = [
    {
      type: "button",
      text: "Cancel",
      onClick: handleCancel,
      size: "small",
      variant: "outlined",
      color: "error",
    },
    {
      type: "button",
      text: "Save",
      loading,
      buttonType: "submit",
      size: "small",
      variant: "outlined",
      color: "primary",
    },
  ];

  if (initialLoading)
    return (
      <Paper
        elevation={3}
        sx={{ p: 3, display: "flex", justifyContent: "center", mt: 2 }}
      >
        <CircularProgress />
      </Paper>
    );

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <HeaderList
        title={isEditMode ? "Update Vendor Bill" : "Add Vendor Bill"}
        isBackButton
        url="/vendorbill/list"
        type="card"
        actions={actions}
        isDivider
        headerSx={{ height: 50 }}
      />
      <Paper elevation={3} sx={{ p: 3, mt: 1 }}>
        <Typography sx={{ fontSize: ".9rem" }} color={"gray"}>
          Vendor Bill Details
        </Typography>
        <br />
        <Grid container spacing={2}>
          {/* Vendor Dropdown */}
          <Grid item xs={12} sm={4}>
            <FormControl
              fullWidth
              required
              error={!!errors.vendorID}
              size="small"
              variant="outlined"
            >
              <InputLabel id="vendor-label">Vendor</InputLabel>
              <Controller
                name="vendorID"
                control={control}
                rules={{ required: "Vendor required" }}
                render={({ field }) => (
                  <Select {...field} labelId="vendor-label" label="Vendor">
                    <MenuItem value="" disabled>
                      Select Vendor
                    </MenuItem>
                    {vendorOptions.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.vendorName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.vendorID && (
                <FormHelperText>{errors.vendorID.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Controller
              name="billDate"
              control={control}
              render={() => (
                <WSTextField
                  id="billDate"
                  control={control}
                  name="billDate"
                  label="Bill Date *"
                  type={TextType.Date}
                  sx={{ color: "gray" }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              name="dueDate"
              control={control}
              render={() => (
                <WSTextField
                  id="dueDate"
                  control={control}
                  name="dueDate"
                  label="Due Date *"
                  type={TextType.Date}
                  sx={{ color: "gray" }}
                />
              )}
            />
          </Grid>
        </Grid>

        <br />
        {fields.map((field, i) => (
          <Grid container spacing={2} key={field.id} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Controller
                name={`items.${i}.itemName`}
                control={control}
                render={() => (
                  <WSTextField
                    id={`itemName-${i}`}
                    control={control}
                    name={`items.${i}.itemName`}
                    label="Item Name *"
                    type={TextType.Text}
                    sx={{ color: "gray" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Controller
                name={`items.${i}.qty`}
                control={control}
                render={() => (
                  <WSTextField
                    id={`qty-${i}`}
                    control={control}
                    name={`items.${i}.qty`}
                    label="Qty *"
                    type={TextType.Number}
                    sx={{ color: "gray" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Controller
                name={`items.${i}.rate`}
                control={control}
                render={() => (
                  <WSTextField
                    id={`rate-${i}`}
                    control={control}
                    name={`items.${i}.rate`}
                    label="Rate *"
                    type={TextType.Number}
                    sx={{ color: "gray" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Controller
                name={`items.${i}.tax`}
                control={control}
                render={() => (
                  <WSTextField
                    id={`tax-${i}`}
                    control={control}
                    name={`items.${i}.tax`}
                    label="Tax %"
                    type={TextType.Number}
                    sx={{ color: "gray" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={1} sm={1}>
              <IconButton
                onClick={() => remove(i)}
                disabled={fields.length === 1}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Button
          sx={{ mt: 1, color: "grey" }}
          startIcon={<AddIcon />}
          onClick={() => append({ itemName: "", qty: 1, rate: 0, tax: 0 })}
        >
          Add Item
        </Button>

        <Typography sx={{ fontWeight: "bold", pt: 2, pb: 1 }} color="grey">
          Totals
        </Typography>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item xs={6} sm={3}>
            <WSTextField
              id="subtotal"
              control={control}
              name="subtotal"
              label="Subtotal"
              type={TextType.Number}
              disabled
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <WSTextField
              id="taxAmount"
              control={control}
              name="taxAmount"
              label="Tax Amount"
              type={TextType.Number}
              disabled
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <WSTextField
              id="totalAmount"
              control={control}
              name="totalAmount"
              label="Total Amount"
              type={TextType.Number}
              disabled
            />
          </Grid>
        </Grid>
      </Paper>
    </form>
  );
});


